/* svelte-i18n integration — translations are fetched at runtime from
 * Django so translators can edit catalogs in django-rosetta and have
 * edits appear after a single page reload (no rebuild required).
 *
 * Wire flow:
 *   1. `initI18n()` runs once at app boot (`main.ts`). It registers an
 *      empty dictionary for every supported language, picks the initial
 *      locale (saved → navigator → "en"), and starts svelte-i18n.
 *   2. The first time `$_("…")` is called for a given locale, an
 *      `await`able loader fetches `/api/v1/i18n/<lang>.json`,
 *      conditionally GETs subsequent times using the cached `ETag`.
 *   3. The dictionary is also cached in `localStorage` keyed by
 *      `oe_i18n_<lang>` (with the ETag) so a warm reload skips the
 *      network entirely until the catalog changes.
 *   4. EN is the fallback. Languages with no translated strings yet
 *      will return an empty dict and svelte-i18n falls back to EN.
 */

import { get } from "svelte/store";
import {
  addMessages,
  init,
  getLocaleFromNavigator,
  locale,
  register,
  waitLocale,
} from "svelte-i18n";

const STORAGE_KEY = "oe_lang";
const CACHE_PREFIX = "oe_i18n_"; // oe_i18n_<lang> → { etag, dict }
const ENDPOINT = (lang: string) => `/api/v1/i18n/${lang}.json`;

export type Lang =
  | "en" | "cs" | "sk" | "de" | "pl" | "ru" | "uk"
  | "lv" | "hy" | "az" | "ka" | "hu" | "bg" | "et" | "lt";

/** Languages exposed in the public-page switcher. `label` is each language's
 *  own self-label and is intentionally not translated. `short` is the
 *  two-letter code shown in the compact selector. */
export const LANGUAGES: { code: Lang; label: string; short: string }[] = [
  { code: "en", label: "English",     short: "EN" },
  { code: "cs", label: "Čeština",     short: "CZ" },
  { code: "sk", label: "Slovenčina",  short: "SK" },
  { code: "de", label: "Deutsch",     short: "DE" },
  { code: "pl", label: "Polski",      short: "PL" },
  { code: "ru", label: "Русский",     short: "RU" },
  { code: "uk", label: "Українська",  short: "UA" },
  { code: "lv", label: "Latviešu",    short: "LV" },
  { code: "hy", label: "Հայերեն",     short: "HY" },
  { code: "az", label: "Azərbaycan",  short: "AZ" },
  { code: "ka", label: "ქართული",     short: "KA" },
  { code: "hu", label: "Magyar",      short: "HU" },
  { code: "bg", label: "Български",   short: "BG" },
  { code: "et", label: "Eesti",       short: "ET" },
  { code: "lt", label: "Lietuvių",    short: "LT" },
];

const SUPPORTED: Set<Lang> = new Set(LANGUAGES.map((l) => l.code));

type CacheEntry = { etag: string; dict: Record<string, string> };

function readCache(lang: Lang): CacheEntry | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CACHE_PREFIX + lang);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.etag === "string" && parsed.dict && typeof parsed.dict === "object") {
      return parsed as CacheEntry;
    }
  } catch {
    /* ignore */
  }
  return null;
}

function writeCache(lang: Lang, entry: CacheEntry): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(CACHE_PREFIX + lang, JSON.stringify(entry));
  } catch {
    /* quota / disabled — silently degrade to in-memory only */
  }
}

/** Fetch a language catalog from Django, using ETag conditional GET. */
async function fetchCatalog(lang: Lang): Promise<Record<string, string>> {
  const cached = readCache(lang);
  const headers: Record<string, string> = { Accept: "application/json" };
  if (cached?.etag) headers["If-None-Match"] = cached.etag;

  let res: Response;
  try {
    res = await fetch(ENDPOINT(lang), { headers, credentials: "same-origin" });
  } catch {
    // Network failure: fall back to whatever we cached, or empty.
    return cached?.dict ?? {};
  }

  if (res.status === 304 && cached) {
    return cached.dict;
  }
  if (!res.ok) {
    // 404 / 5xx: don't poison the cache, return whatever we have.
    return cached?.dict ?? {};
  }

  const dict = (await res.json()) as Record<string, string>;
  const etag = res.headers.get("ETag") ?? "";
  if (etag) writeCache(lang, { etag, dict });
  return dict;
}

/* ── Public-page locale scope ──────────────────────────────────────────────
 *
 * A public showcase page has its own language switcher, and it must stay a
 * *viewing* choice: the visitor is reading someone else's storefront, not
 * configuring their own account. Without this, an owner who previews their
 * page in Latvian comes back to find the admin UI — and their stored
 * `user.language` — switched to Latvian too.
 *
 * While the scope is active, locale changes still re-render the page but are
 * neither written to localStorage nor pushed to `/api/v1/me`; leaving the
 * scope restores whatever interface language the user actually chose.
 */
let publicScope = false;
let localeBeforePublicScope: Lang | null = null;
let pendingScopeExit: ReturnType<typeof setTimeout> | null = null;

/** Called when a public (`/pub/...`) page mounts. */
export function enterPublicLocaleScope(): void {
  if (pendingScopeExit) {
    clearTimeout(pendingScopeExit);
    pendingScopeExit = null;
  }
  if (publicScope) return;
  publicScope = true;
  localeBeforePublicScope = readSavedLang() ?? ((get(locale) as Lang) ?? null);
}

/** Called when a public page unmounts — puts the interface language back.
 *
 *  Deferred by a tick on purpose: moving between two public pages destroys one
 *  component and mounts the next within the same turn, and the visitor's
 *  chosen language must survive that hand-off instead of snapping back. */
export function exitPublicLocaleScope(): void {
  if (!publicScope || pendingScopeExit) return;
  pendingScopeExit = setTimeout(() => {
    pendingScopeExit = null;
    if (!publicScope) return;
    publicScope = false;
    const back = localeBeforePublicScope;
    localeBeforePublicScope = null;
    if (back && get(locale) !== back) locale.set(back);
  }, 0);
}

/* The language a company page opens in is the company's choice — its local-
 * market language at home, its foreign-market language abroad (`page_language`
 * from `/pub/<slug>/profile`). Once the visitor picks a language in the page's
 * switcher, that pick wins for the rest of the browser session. */
const PUB_PICK_KEY = "oe_pub_lang";

function readPublicPick(): Lang | null {
  if (typeof window === "undefined") return null;
  try {
    const v = window.sessionStorage.getItem(PUB_PICK_KEY);
    return v && (SUPPORTED as Set<string>).has(v) ? (v as Lang) : null;
  } catch {
    return null;
  }
}

/** The visitor chose a language in a public page's switcher. */
export function pickPublicLanguage(lang: Lang): void {
  try {
    window.sessionStorage.setItem(PUB_PICK_KEY, lang);
  } catch {
    /* storage disabled — the pick still applies to this page */
  }
  locale.set(lang);
}

/** Open a public page in the company's language for this visitor, unless the
 *  visitor has already picked one. */
export function applyPublicPageLanguage(pageLanguage: string | undefined): void {
  const lang =
    readPublicPick() ??
    (pageLanguage && (SUPPORTED as Set<string>).has(pageLanguage) ? (pageLanguage as Lang) : null);
  if (lang && get(locale) !== lang) locale.set(lang);
}

function readSavedLang(): Lang | null {
  if (typeof window === "undefined") return null;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v && (SUPPORTED as Set<string>).has(v) ? (v as Lang) : null;
  } catch {
    return null;
  }
}

function pickInitialLocale(): Lang {
  const saved = readSavedLang();
  if (saved) return saved;
  const nav = (getLocaleFromNavigator() ?? "").toLowerCase();
  for (const code of SUPPORTED) {
    if (nav === code || nav.startsWith(`${code}-`)) return code;
  }
  return "en";
}

export function initI18n(): Promise<void> {
  // Register one async loader per language. svelte-i18n will await the
  // loader the first time the locale is activated, then cache the result
  // for subsequent reads. We also seed `addMessages` from localStorage
  // synchronously so the very first paint already has translations
  // available on warm reloads.
  for (const { code } of LANGUAGES) {
    const cached = readCache(code);
    if (cached) addMessages(code, cached.dict);
    register(code, () => fetchCatalog(code).then((dict) => ({ default: dict })));
  }

  const initialLocale = pickInitialLocale();
  init({
    fallbackLocale: "en",
    initialLocale,
  });

  // Eagerly load the initial locale before the app renders any `$_(...)`
  // calls. svelte-i18n keeps `locale` unset while an async loader is
  // flushing, and formatting during that window throws at runtime.
  const ready = waitLocale().catch(async () => {
    // If the loader itself fails unexpectedly, still set the locale so
    // formatting falls back to msgids instead of crashing the app.
    await locale.set(initialLocale);
  });

  if (typeof window !== "undefined") {
    locale.subscribe((value) => {
      if (!value) return;
      document.documentElement.lang = value;
      // A language picked on someone's public showcase page is a viewing
      // choice, not an account setting — see enterPublicLocaleScope.
      if (publicScope) return;
      try {
        window.localStorage.setItem(STORAGE_KEY, value);
      } catch {
        /* ignore storage errors */
      }
      // Persist to server (fire-and-forget — don't block UI on this)
      fetch("/api/v1/me", {
        method: "PATCH",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", "X-CSRFToken": _getCsrf() },
        body: JSON.stringify({ language: value }),
      }).catch(() => { /* silent - offline / unauthenticated */ });
    });
  }

  return ready;
}

function _getCsrf(): string {
  if (typeof document === "undefined") return "";
  const match = document.cookie.match(/csrftoken=([^;]+)/);
  return match ? match[1] : "";
}

export { locale };
